import React, { Component } from 'react';
import moment from 'moment';
import CalendarPopover from './CalendarPopover';
import { isAllLocations, getLocationBadgeColor } from '../../../util/locationUtils';
import './CalendarGrid.css';

const HOURS = [
    '8 AM', '9 AM', '10 AM', '11 AM', '12 PM',
    '1 PM', '2 PM', '3 PM', '4 PM', '5 PM'
];

class CalendarGrid extends Component {
    state = {
        popoverJob: null,
        popoverPosition: { top: 0, left: 0 },
        morePopover: null, // { dateStr, jobs, top, left }
    };

    // ---- Data helpers ----

    groupJobsByDate = () => {
        const map = {};
        (this.props.displayWorkTable || []).forEach(job => {
            const key = job.startDate; // YYYY-MM-DD
            if (!map[key]) map[key] = [];
            map[key].push(job);
        });
        return map;
    };

    getDateColumns = () => {
        const { range, startDate } = this.props;
        const start = moment(startDate);
        if (range === 'day') return [start.format('YYYY-MM-DD')];
        // week — 7 days starting from startDate (already start-of-week)
        const cols = [];
        for (let i = 0; i < 7; i++) {
            cols.push(moment(start).add(i, 'days').format('YYYY-MM-DD'));
        }
        return cols;
    };

    isDateBlocked = (dateStr) => {
        return (this.props.blockedPeriodsInRange || []).some(p => {
            const pStart = moment.utc(p.start_date).local().format('YYYY-MM-DD');
            const pEnd = moment.utc(p.end_date).local().format('YYYY-MM-DD');
            return dateStr >= pStart && dateStr <= pEnd;
        });
    };

    getBlockedReason = (dateStr) => {
        const match = (this.props.blockedPeriodsInRange || []).find(p => {
            const pStart = moment.utc(p.start_date).local().format('YYYY-MM-DD');
            const pEnd = moment.utc(p.end_date).local().format('YYYY-MM-DD');
            return dateStr >= pStart && dateStr <= pEnd;
        });
        return match ? match.reason : '';
    };

    // ---- Popover ----

    handleBlockClick = (e, job) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        this.setState({
            popoverJob: job,
            popoverPosition: {
                top: rect.bottom + 4,
                left: rect.left,
            },
            morePopover: null,
        });
    };

    handleClosePopover = () => {
        this.setState({ popoverJob: null });
    };

    handleMoreClick = (e, dateStr, jobs) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        this.setState({
            morePopover: {
                dateStr,
                jobs,
                top: rect.bottom + 4,
                left: rect.left,
            },
        });
    };

    handleCloseMore = () => {
        this.setState({ morePopover: null });
    };

    // ---- Render helpers ----

    renderJobBlock = (job, flexGrow) => {
        const isDealer = job.requestType === 'dealer';
        const showLoc = isAllLocations(this.props.selectedLocationId) &&
            Array.isArray(this.props.locations) && this.props.locations.length > 1;
        const locId = job.location_id || job.locationId;

        const vehicle = [job.vehicleYear, job.vehicleMake, job.vehicleModel]
            .filter(Boolean).join(' ') || 'Vehicle';
        const service = [job.category, job.serviceDescription].filter(Boolean).join(' - ') || 'Service';
        const customer = job.customerName || job.dealerName || '';

        const isStarted = job.workStatus === 'started';
        const isFinished = job.workStatus === 'finished' || job.workStatus === 'completed';

        return (
            <div
                key={job.viewKey}
                className={`cg-job-block ${isDealer ? 'dealer' : 'installer'}`}
                style={{ flex: `${flexGrow || 1} 1 0` }}
                onClick={(e) => this.handleBlockClick(e, job)}
                title={`${service} — ${vehicle}`}
            >
                <div className="cg-job-vehicle">{vehicle}</div>
                <div className="cg-job-service">{service}</div>
                {showLoc && locId && (
                    <span
                        className="cg-loc-badge"
                        style={{ backgroundColor: getLocationBadgeColor(locId, this.props.locations) }}
                    >
                        {job.locationName || job.location_name || ''}
                    </span>
                )}
                <div className="cg-job-customer">{customer}</div>
                <div className="cg-job-meta">
                    {isStarted && (
                        <span className="cg-job-status-badge started">In Progress</span>
                    )}
                    {isFinished && (
                        <span className="cg-job-status-badge completed">Completed</span>
                    )}
                    {job.isMultiDay && (
                        <span className="cg-job-day-badge">Day {job.currentDay}/{job.totalDays}</span>
                    )}
                </div>
            </div>
        );
    };

    renderMonthJobBar = (job) => {
        const isDealer = job.requestType === 'dealer';
        const vehicle = [job.vehicleMake, job.vehicleModel].filter(Boolean).join(' ') || 'Vehicle';
        const service = job.category || 'Service';
        const showLoc = isAllLocations(this.props.selectedLocationId) &&
            Array.isArray(this.props.locations) && this.props.locations.length > 1;
        const locId = job.location_id || job.locationId;

        return (
            <div
                key={job.viewKey}
                className={`cg-month-bar ${isDealer ? 'dealer' : 'installer'}`}
                onClick={(e) => this.handleBlockClick(e, job)}
                title={`${service} | ${vehicle}`}
            >
                {showLoc && locId && (
                    <span
                        className="cg-month-bar-loc-dot"
                        style={{ backgroundColor: getLocationBadgeColor(locId, this.props.locations) }}
                    />
                )}
                <span className="cg-month-bar-text">{service} | {vehicle}</span>
            </div>
        );
    };

    // ---- Day/Week grid ----

    renderDayWeekGrid = () => {
        const columns = this.getDateColumns();
        const jobsByDate = this.groupJobsByDate();
        const todayStr = moment().format('YYYY-MM-DD');

        return (
            <div className="calendar-grid-view">
                {/* Header */}
                <div className="cg-header">
                    <div className="cg-time-spacer" />
                    {columns.map(dateStr => {
                        const m = moment(dateStr);
                        const isToday = dateStr === todayStr;
                        return (
                            <div key={dateStr} className={`cg-day-header ${isToday ? 'cg-today' : ''}`}>
                                <div className="cg-day-name">{m.format('ddd')}</div>
                                <div className="cg-day-number">{m.format('D')}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Body */}
                <div className="cg-body">
                    {/* Time column */}
                    <div className="cg-time-col">
                        {HOURS.map(h => (
                            <div key={h} className="cg-time-slot">{h}</div>
                        ))}
                    </div>

                    {/* Day columns */}
                    {columns.map(dateStr => {
                        const isToday = dateStr === todayStr;
                        const blocked = this.isDateBlocked(dateStr);
                        const reason = blocked ? this.getBlockedReason(dateStr) : '';
                        const dayJobs = jobsByDate[dateStr] || [];

                        return (
                            <div key={dateStr} className={`cg-day-col ${isToday ? 'cg-today-col' : ''}`}>
                                {/* Hour grid lines */}
                                {HOURS.map(h => (
                                    <div key={h} className="cg-hour-line" />
                                ))}

                                {/* Blocked overlay */}
                                {blocked && (
                                    <div className="cg-blocked-overlay">
                                        <div className="cg-blocked-text">
                                            Closed{reason ? ` — ${reason}` : ''}
                                        </div>
                                    </div>
                                )}

                                {/* Jobs — stacked vertically, proportional to duration */}
                                {!blocked && dayJobs.length > 0 && (
                                    <div className="cg-jobs-container">
                                        {dayJobs.map(job => {
                                            const duration = Math.max(job.estimate || 0.5, 0.1);
                                            return this.renderJobBlock(job, duration);
                                        })}
                                    </div>
                                )}

                                {!blocked && dayJobs.length === 0 && (
                                    <div className="cg-empty-day">—</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // ---- Month grid ----

    renderMonthGrid = () => {
        const { startDate } = this.props;
        const monthStart = moment(startDate).startOf('month');
        const monthEnd = moment(startDate).endOf('month');
        const calStart = moment(monthStart).startOf('week');  // Sunday before month
        const calEnd = moment(monthEnd).endOf('week');         // Saturday after month
        const todayStr = moment().format('YYYY-MM-DD');

        const jobsByDate = this.groupJobsByDate();
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        // Build array of all dates to show
        const cells = [];
        let cursor = moment(calStart);
        while (cursor.isSameOrBefore(calEnd, 'day')) {
            cells.push(cursor.format('YYYY-MM-DD'));
            cursor.add(1, 'day');
        }

        return (
            <div className="calendar-grid-view">
                <div className="cg-month">
                    {/* Header row */}
                    {dayNames.map(d => (
                        <div key={d} className="cg-month-header-cell">{d}</div>
                    ))}

                    {/* Day cells */}
                    {cells.map(dateStr => {
                        const m = moment(dateStr);
                        const isOutside = m.month() !== monthStart.month();
                        const isToday = dateStr === todayStr;
                        const blocked = this.isDateBlocked(dateStr);
                        const reason = blocked ? this.getBlockedReason(dateStr) : '';
                        const dayJobs = jobsByDate[dateStr] || [];
                        const maxVisible = 3;
                        const extraCount = dayJobs.length - maxVisible;

                        let cellClass = 'cg-month-cell';
                        if (isOutside) cellClass += ' cg-outside';
                        if (isToday) cellClass += ' cg-today-cell';
                        if (blocked) cellClass += ' cg-blocked-cell';

                        return (
                            <div key={dateStr} className={cellClass}>
                                <div>
                                    <span className="cg-month-date">{m.format('D')}</span>
                                    {blocked && <span className="cg-month-closed-label">{reason || 'Closed'}</span>}
                                </div>
                                {!blocked && dayJobs.slice(0, maxVisible).map(job => this.renderMonthJobBar(job))}
                                {!blocked && extraCount > 0 && (
                                    <div
                                        className="cg-month-more"
                                        onClick={(e) => this.handleMoreClick(e, dateStr, dayJobs.slice(maxVisible))}
                                    >
                                        +{extraCount} more
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    render() {
        const { range } = this.props;
        const { popoverJob, popoverPosition, morePopover } = this.state;

        return (
            <div style={{ position: 'relative' }}>
                {(range === 'day' || range === 'week') && this.renderDayWeekGrid()}
                {range === 'month' && this.renderMonthGrid()}

                {/* Job popover */}
                {popoverJob && (
                    <CalendarPopover
                        job={popoverJob}
                        position={popoverPosition}
                        onClose={this.handleClosePopover}
                        onViewDetails={this.props.onDisplayDetails}
                        locations={this.props.locations}
                        selectedLocationId={this.props.selectedLocationId}
                    />
                )}

                {/* "+N more" popover for month view */}
                {morePopover && (
                    <div
                        className="cg-more-popover"
                        style={{
                            position: 'fixed',
                            top: morePopover.top,
                            left: morePopover.left,
                            zIndex: 100,
                        }}
                    >
                        {morePopover.jobs.map(job => this.renderMonthJobBar(job))}
                        <div
                            className="cg-month-more"
                            onClick={this.handleCloseMore}
                            style={{ textAlign: 'center', marginTop: 4 }}
                        >
                            Close
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default CalendarGrid;
